import {
  MenuItem,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Select as RffSelect, TextField } from 'mui-rff';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import supersub from 'remark-supersub';

import { FeedbackContext } from '../../../../constants/feedbackConstants';
import { processQuestion, userEditSave } from '../../../../services/dealGPT';
import type { ProjectRunAnalysisQuestion, RubricCore } from '../../../../Types/dealGPT';
import { ProcessReason, UpdatedReason } from '../../../../Types/enums';
import { FeedbackInterface } from '../../../../Types/feedback';
import { formatDate } from '../../../../util/dateUtils';
import { renderMessage } from '../../../../util/messageUtils';
import { AugmentedResponseWithSources } from '../../../DealGPT/SourcesDisplay';
import Feedback from '../../../Feedback';
import { AnalyticsEvent, useAnalytics } from '../../../Providers/AnalyticsProvider';
import { useFeatures } from '../../../Providers/FeatureProvider';
import { useNotifications } from '../../../Providers/NotificationsProvider';
import ReadMoreText from '../../PhaseTwo/ReadMoreText';
import QuestionStatus from '../../QuestionStatus';
import MoreOptionsMenu from '../MoreOptionsMenu';
import ReferenceDocuments from '../ReferenceDocuments';
import SectionButtons from '../SectionButtons';
import UnprocessedChangesDot from '../UnprocessedChangesDot';
import RubricRating from './RubricRating';

const StyledTableHeaderCell = styled(TableCell)({
  color: '#757575',
  fontWeight: '700',
  textTransform: 'uppercase',
  paddingBottom: '8px',
});

const StyledSelect = styled(RffSelect)({
  paddingRight: '16px',

  '& .MuiSelect-select': {
    padding: '10px',
    color: '#092B49',
    fontSize: '14px',
  },
});

const ReactMarkdownWrapper = styled('div')({
  fontSize: '14px',
  lineHeight: '1.5rem',
  letterSpacing: '0.25px',
  'a, a:visited, a:hover, a:focus': {
    color: 'inherit',
  },
});

interface RubricRowProps {
  projectId: string;
  dimensionName: string;
  textfieldName: string;
  selectName: string;
  versions: ProjectRunAnalysisQuestion[];
  refetchQuestions: () => void;
  setIsLoadingThis: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingApplyChanges: React.Dispatch<React.SetStateAction<boolean>>;
  setDimensionValues: React.Dispatch<
    React.SetStateAction<{
      [dimension: string]: number;
    }>
  >;
  setSectionUnprocessedDownstreamChanges: React.Dispatch<React.SetStateAction<boolean>>;
  handleFeedbackSubmit: (feedback: FeedbackInterface, projectAnalysisQuestionId: string) => void;
  handleValidateFindingsClick: (questionId: string) => void;
  rubricType: string;
  dimensionRubricCore: RubricCore[] | undefined;
}

const RubricRow: React.FC<RubricRowProps> = ({
  projectId,
  dimensionName,
  textfieldName,
  selectName,
  versions,
  refetchQuestions,
  setIsLoadingThis,
  setIsLoadingApplyChanges,
  setDimensionValues,
  setSectionUnprocessedDownstreamChanges,
  handleFeedbackSubmit,
  handleValidateFindingsClick,
  rubricType,
  dimensionRubricCore,
}) => {
  const [analysisQuestionData, setAnalysisQuestionData] = useState<ProjectRunAnalysisQuestion>(
    versions[0]
  );
  const [questionVersions, setQuestionVersions] = useState<ProjectRunAnalysisQuestion[]>(versions);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { refetchNotifications } = useNotifications();

  const features = useFeatures();
  const analytics = useAnalytics();
  const questionStatuses: string[] = [];

  if (analysisQuestionData.validationResponse) {
    questionStatuses.push('VALIDATED');
  }

  // This is used when the user clicks the save button
  const handleSave = useCallback(
    async (
      values: Record<string, string>,
      reason?: UpdatedReason,
      processReason?: ProcessReason
    ) => {
      const tempAnalysisQuestion: ProjectRunAnalysisQuestion = { ...analysisQuestionData };
      tempAnalysisQuestion.value = values[selectName];
      tempAnalysisQuestion.response = values[textfieldName];

      if (reason) {
        tempAnalysisQuestion.updatedReason = reason;
      }

      let updatedQuestion = tempAnalysisQuestion;
      if (reason === UpdatedReason.USER_EDIT) {
        try {
          const updatedResult = await userEditSave(tempAnalysisQuestion);

          setAnalysisQuestionData(updatedResult);
          updatedQuestion = updatedResult;
          enqueueSnackbar('Content saved successfully', { variant: 'success' });
          refetchQuestions();
        } catch {
          enqueueSnackbar('Failed to save Rubric. Please try again later.', {
            variant: 'error',
          });
        }
      }

      if (
        tempAnalysisQuestion.updatedReason === UpdatedReason.REPROCESS ||
        processReason === ProcessReason.APPLY_CHANGES
      ) {
        try {
          enqueueSnackbar('Your content is being processed', { variant: 'info' });
          setIsLoadingThis(true);
          if (processReason === ProcessReason.APPLY_CHANGES) {
            setIsLoadingApplyChanges(true);
          }
          await processQuestion(updatedQuestion, processReason);
          refetchNotifications();
          refetchQuestions();
        } catch {
          enqueueSnackbar('Failed to save Rubric. Please try again later.', {
            variant: 'error',
          });
        } finally {
          setIsLoadingThis(false);
          setIsLoadingApplyChanges(false);
        }
      }
    },
    [
      analysisQuestionData,
      selectName,
      textfieldName,
      refetchQuestions,
      setIsLoadingThis,
      setIsLoadingApplyChanges,
      refetchNotifications,
    ]
  );

  const onSubmit = async () => {
    // const tempAnalysisQuestion = await handleSave(values, UpdatedReason.REPROCESS);
    // if (tempAnalysisQuestion) {
    //   enqueueSnackbar('Your content is being processed', { variant: 'success' });
    //   await processQuestion(tempAnalysisQuestion);
    //   refetchQuestions();
    // }
  };

  const handleVersionChange = useCallback(
    async (value: string) => {
      const version = versions.find((v) => v.id === value);
      analytics.event(
        AnalyticsEvent.WM_IA_A_RUBRIC_QUESTION_VERSION_CHANGE,
        {
          id: value,
          value: versions[0].pageName,
        },
        {
          projectId,
          runId: versions[0].runId,
          scope: versions[0].scope,
          oldVersionId: analysisQuestionData.id,
          newVersionId: value,
        }
      );
      if (version) {
        setAnalysisQuestionData(version);

        const value = version.value ?? null;
        if (value !== null) {
          setDimensionValues((prev) => ({
            ...prev,
            [dimensionName]: parseInt(value, 10),
          }));
        }
      }
    },
    [versions, dimensionName, setDimensionValues, analytics, analysisQuestionData.id, projectId]
  );

  useEffect(() => {
    setQuestionVersions(versions);

    if (versions[0]) {
      if (!analysisQuestionData || analysisQuestionData.id === undefined) {
        setAnalysisQuestionData(versions[0]);
        const value = versions[0].value ?? null;
        if (value !== null) {
          setDimensionValues((prev) => ({
            ...prev,
            [dimensionName]: parseInt(value, 10),
          }));
        }
      }
      if (versions[0].unprocessedDownstreamChanges) {
        setSectionUnprocessedDownstreamChanges(true);
      }
    }
  }, [
    versions,
    setSectionUnprocessedDownstreamChanges,
    analysisQuestionData,
    dimensionName,
    setDimensionValues,
  ]);

  const initialValues = useMemo(
    () => ({
      [selectName]: analysisQuestionData.value,
      [textfieldName]: analysisQuestionData.response,
    }),
    [selectName, textfieldName, analysisQuestionData.value, analysisQuestionData.response]
  );

  const displayRubricValue = (value: string | null | undefined) => {
    return value === undefined || value === null || value === '0' ? (
      <span style={{ color: '#F52C00' }}>N/A</span>
    ) : (
      value
    );
  };

  const scaleValues = useMemo(
    () => (rubricType === 'LMH' ? ['', 'Low', 'Medium', 'High'] : ['', '1', '2', '3', '4', '5']),
    [rubricType]
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, values }) => (
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Select
              value={
                questionVersions.some((v) => v.id === analysisQuestionData?.id)
                  ? analysisQuestionData.id
                  : questionVersions[0].id
              }
              onChange={(event) => handleVersionChange(event.target.value as string)}
              size="small"
            >
              {questionVersions.map((version) => (
                <MenuItem
                  value={version.id}
                  selected={version.id === analysisQuestionData.id}
                  key={version.id}
                >
                  Generated on {version.createdAt && formatDate(version.createdAt)} |{' '}
                  {version.updatedReason}
                </MenuItem>
              ))}
            </Select>
            {questionVersions.length > 0 && questionVersions[0].unprocessedDownstreamChanges && (
              <UnprocessedChangesDot />
            )}
          </div>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableHeaderCell>Dimension</StyledTableHeaderCell>
                <StyledTableHeaderCell sx={{ textAlign: 'center' }}>Rating</StyledTableHeaderCell>
                <StyledTableHeaderCell>Explanation</StyledTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={2} sx={{ borderBottom: 'none' }}></TableCell>
                <TableCell sx={{ borderBottom: 'none', padding: 0, paddingTop: '10px' }}>
                  <div style={{ display: 'flex' }}>
                    {analysisQuestionData.validationResponse && (
                      <div style={{ margin: 'auto', paddingRight: '15px' }}>
                        <QuestionStatus statuses={questionStatuses} />
                      </div>
                    )}
                    <SectionButtons
                      projectId={projectId}
                      handleSave={handleSave}
                      values={values}
                      hasGenerated={true}
                      handleEdit={setIsEditing}
                      isEditing={isEditing}
                      question={analysisQuestionData}
                      showGenerateButton={false}
                    />
                    <div style={{ marginLeft: '5px' }}>
                      <MoreOptionsMenu
                        projectId={projectId}
                        analysisQuestionData={analysisQuestionData}
                        handleSave={handleSave}
                        values={values}
                        isEditing={isEditing}
                        handleValidateFindingsClick={handleValidateFindingsClick}
                      />
                    </div>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow sx={{ verticalAlign: 'top' }}>
                <TableCell
                  sx={{
                    width: '250px',
                    fontWeight: '300',
                    color: '#092B49',
                    borderBottom: 'none',
                    height: '1px',
                  }}
                >
                  <Typography>{dimensionName}</Typography>
                </TableCell>
                <TableCell sx={{ width: '80px', borderBottom: 'none' }}>
                  {isEditing ? (
                    <StyledSelect name={selectName}>
                      {scaleValues.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value || 'N/A'}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  ) : (
                    <div style={{ display: 'inline-flex', verticalAlign: 'top', width: '80px' }}>
                      <Typography
                        sx={{
                          width: '100%',
                          fontSize: '14px',
                          fontWeight: 700,
                          textAlign: 'center',
                        }}
                      >
                        {displayRubricValue(analysisQuestionData.value)}
                      </Typography>
                    </div>
                  )}
                </TableCell>
                <TableCell sx={{ padding: '0px', paddingRight: '16px', borderBottom: 'none' }}>
                  {isEditing ? (
                    <TextField name={textfieldName} multiline sx={{ marginTop: '10px' }} />
                  ) : (
                    <ReactMarkdownWrapper>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, supersub]}
                        components={{
                          sup: ({ children }) => (
                            <AugmentedResponseWithSources
                              text={children.toString()}
                              sources={analysisQuestionData.documents ?? []}
                            />
                          ),
                        }}
                      >
                        {renderMessage({
                          message: analysisQuestionData.response || '',
                          sources: analysisQuestionData.documents || [],
                        })}
                      </ReactMarkdown>
                      {analysisQuestionData?.validationResponse && (
                        <div>
                          <hr />
                          <Typography>Validation</Typography>
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm, supersub]}
                            components={{
                              sup: ({ children }) => (
                                <AugmentedResponseWithSources
                                  text={children.toString()}
                                  sources={analysisQuestionData.validationSources ?? []}
                                />
                              ),
                            }}
                          >
                            {renderMessage({
                              message: analysisQuestionData.validationResponse || '',
                              sources: analysisQuestionData.validationSources || [],
                            })}
                          </ReactMarkdown>
                        </div>
                      )}
                      <Feedback
                        initialFeedback={
                          analysisQuestionData.analysisQuestionFeedback as
                            | FeedbackInterface
                            | undefined
                        }
                        context={FeedbackContext.ANALYSIS_QUESTION}
                        onFeedbackSubmit={(feedback) =>
                          handleFeedbackSubmit(feedback, analysisQuestionData.id)
                        }
                      />
                    </ReactMarkdownWrapper>
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={3} sx={{ borderBottom: 'none', paddingBottom: '40px' }}>
                  <ReferenceDocuments
                    questionId={analysisQuestionData.id}
                    documents={versions[0].documents}
                    showAnalysisText={true}
                  />
                  {analysisQuestionData.validationSources &&
                    analysisQuestionData.validationSources.length > 0 && (
                      <div style={{ marginTop: '1.3em' }}>
                        <ReferenceDocuments
                          questionId={analysisQuestionData.id}
                          documents={analysisQuestionData.validationSources}
                          showReferenceTitle={false}
                          showAnalysisText={true}
                          showValidationTitle={true}
                        />
                      </div>
                    )}
                </TableCell>
              </TableRow>
              {features.deal_gpt_enriched_prompt_display && (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    style={{ paddingTop: 0, paddingBottom: '10px', borderBottom: 'none' }}
                  >
                    <>
                      <Typography sx={{ fontWeight: '700', color: '#092B49' }}>
                        Enriched Prompt:
                      </Typography>
                      <ReadMoreText text={versions[0].enrichedPrompt || ''} maxLength={65} />
                    </>
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={3} sx={{ borderBottom: 'none', paddingBottom: '10px' }}>
                  <RubricRating
                    rubricType={rubricType}
                    rating={analysisQuestionData.value ?? ''}
                    rubricCore={dimensionRubricCore ?? []}
                    loading={!dimensionRubricCore}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </form>
      )}
    />
  );
};

export default RubricRow;
